/**
 * SmartAddJobCenter v2.0
 *
 * Global bottom-right indicator showing active SmartAdd jobs and completed notifications.
 * Persists across all routes when rendered in AppShell.
 *
 * v2.0: Enhanced with detailed status, elapsed time, cancel button, no auto-dismiss
 */

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle, X, Check, XCircle, Ban, FileText } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useSmartAddJobsStore, type TrackedJob } from '../store/useSmartAddJobsStore';
import { useCancelSmartAdd } from '../hooks/useSmartAddCallables';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import type { SmartAddJobStatus } from '../types';

// ============================================================================
// Status Configuration
// ============================================================================

interface StatusConfig {
  title: string;
  description: string;
  showSpinner: boolean;
  cancellable: boolean;
}

const STATUS_CONFIG: Record<SmartAddJobStatus | 'pending_creation', StatusConfig> = {
  created: {
    title: 'Document ontvangen',
    description: 'Je document is geüpload en wordt voorbereid voor analyse.',
    showSpinner: true,
    cancellable: true,
  },
  pending_creation: {
    title: 'Document ontvangen',
    description: 'Je document is geüpload en wordt voorbereid voor analyse.',
    showSpinner: true,
    cancellable: true,
  },
  uploaded: {
    title: 'Document analyseren',
    description: 'AI leest de factuur en herkent velden zoals leverancier, bedragen en voertuigen.',
    showSpinner: true,
    cancellable: true,
  },
  queued: {
    title: 'In wachtrij',
    description: 'De analyse start zo.',
    showSpinner: true,
    cancellable: true,
  },
  analyzing: {
    title: 'Gegevens extraheren',
    description: 'We halen relevante gegevens uit het document.',
    showSpinner: true,
    cancellable: true,
  },
  review: {
    title: 'Analyse voltooid',
    description: 'Controleer de gegevens en bevestig.',
    showSpinner: false,
    cancellable: false,
  },
  committed: {
    title: 'Verwerkt',
    description: 'De factuur is succesvol aangemaakt.',
    showSpinner: false,
    cancellable: false,
  },
  failed: {
    title: 'Analyse mislukt',
    description: 'Er is een fout opgetreden.',
    showSpinner: false,
    cancellable: false,
  },
  cancelled: {
    title: 'Analyse geannuleerd',
    description: 'De analyse is gestopt.',
    showSpinner: false,
    cancellable: false,
  },
};

// ============================================================================
// Elapsed Time Hook - REMOVED (Fix 3)
// Timer removed per UX improvement request
// ============================================================================

// ============================================================================
// Active Job Card Component
// ============================================================================

interface ActiveJobCardProps {
  job: TrackedJob;
  onCancel: (uploadId: string, teamId: string) => void;
  isCancelling: boolean;
}

function ActiveJobCard({ job, onCancel, isCancelling }: ActiveJobCardProps) {
  const navigate = useNavigate();
  const config = STATUS_CONFIG[job.status] || STATUS_CONFIG.analyzing;

  const handleClick = () => {
    navigate(`/purchase-invoices/smart-add/${job.uploadId}`);
  };

  const handleCancel = (e: React.MouseEvent) => {
    e.stopPropagation();
    onCancel(job.uploadId, job.teamId);
  };

  return (
    <Card
      className="shadow-lg border-primary/20 bg-background/95 backdrop-blur cursor-pointer transition-all hover:shadow-xl hover:border-primary/40"
      onClick={handleClick}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          {/* Status icon */}
          <div className="flex-shrink-0 mt-0.5">
            {config.showSpinner ? (
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
            ) : job.status === 'cancelled' ? (
              <Ban className="h-5 w-5 text-muted-foreground" />
            ) : (
              <FileText className="h-5 w-5 text-primary" />
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm">{config.title}</p>
            <p className="text-xs text-muted-foreground line-clamp-2">{config.description}</p>

            {/* File name */}
            {job.fileName && (
              <div className="mt-1.5 text-xs text-muted-foreground truncate max-w-[200px]">
                {job.fileName}
              </div>
            )}
          </div>

          {/* Cancel button */}
          {config.cancellable && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-muted-foreground hover:text-destructive flex-shrink-0"
              onClick={handleCancel}
              disabled={isCancelling}
            >
              {isCancelling ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <>
                  <XCircle className="h-3 w-3 mr-1" />
                  Annuleren
                </>
              )}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function SmartAddJobCenter() {
  const navigate = useNavigate();
  const { activeJobs, notifications, dismissNotification } = useSmartAddJobsStore();
  const cancelMutation = useCancelSmartAdd();
  const [cancellingIds, setCancellingIds] = useState<Set<string>>(new Set());

  // Convert Map to array for rendering
  const activeJobsList = Array.from(activeJobs.values());

  // Filter to show running jobs (not completed/committed)
  const runningJobs = activeJobsList.filter(
    (job) =>
      job.status === 'created' ||
      job.status === 'pending_creation' ||
      job.status === 'uploaded' ||
      job.status === 'queued' ||
      job.status === 'analyzing'
  );

  // Also show cancelled jobs briefly
  const cancelledJobs = activeJobsList.filter((job) => job.status === 'cancelled');

  const hasActiveJobs = runningJobs.length > 0 || cancelledJobs.length > 0;
  const hasNotifications = notifications.length > 0;

  const handleCancel = useCallback(
    async (uploadId: string, teamId: string) => {
      setCancellingIds((prev) => new Set(prev).add(uploadId));

      try {
        const result = await cancelMutation.mutateAsync({ teamId, uploadId });
        if (result.success) {
          // UX: No toast on successful cancel - user intentionally cancelled
        } else {
          toast.error(result.error || 'Annuleren mislukt');
        }
      } catch (err) {
        toast.error('Annuleren mislukt');
      } finally {
        setCancellingIds((prev) => {
          const next = new Set(prev);
          next.delete(uploadId);
          return next;
        });
      }
    },
    [cancelMutation]
  );

  const handleNotificationClick = (uploadId: string) => {
    navigate(`/purchase-invoices/smart-add/${uploadId}`);
    dismissNotification(uploadId);
  };

  // Don't render if nothing to show
  if (!hasActiveJobs && !hasNotifications) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 space-y-2 max-w-sm">
      {/* Active/running jobs */}
      {runningJobs.map((job) => (
        <ActiveJobCard
          key={job.uploadId}
          job={job}
          onCancel={handleCancel}
          isCancelling={cancellingIds.has(job.uploadId)}
        />
      ))}

      {/* Cancelled jobs (show briefly) */}
      {cancelledJobs.map((job) => (
        <ActiveJobCard key={job.uploadId} job={job} onCancel={handleCancel} isCancelling={false} />
      ))}

      {/* Completed notifications - NO auto-dismiss */}
      {notifications.map((notification) => (
        <Card
          key={notification.uploadId}
          className={cn(
            'shadow-lg cursor-pointer transition-all hover:shadow-xl bg-background/95 backdrop-blur',
            notification.status === 'review'
              ? 'border-green-500/30 hover:border-green-500/50'
              : 'border-destructive/30 hover:border-destructive/50'
          )}
          onClick={() => handleNotificationClick(notification.uploadId)}
        >
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              {notification.status === 'review' ? (
                <div className="rounded-full bg-green-100 dark:bg-green-900/30 p-1.5 flex-shrink-0">
                  <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
                </div>
              ) : (
                <div className="rounded-full bg-destructive/10 p-1.5 flex-shrink-0">
                  <AlertCircle className="h-4 w-4 text-destructive" />
                </div>
              )}

              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm">
                  {notification.status === 'review' ? 'Analyse voltooid' : 'Analyse mislukt'}
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  {notification.status === 'review'
                    ? 'Klik om de gegevens te controleren'
                    : notification.error?.message || 'Er is een fout opgetreden'}
                </p>
                {notification.fileName && (
                  <p className="text-xs text-muted-foreground truncate mt-0.5">
                    {notification.fileName}
                  </p>
                )}
              </div>

              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 flex-shrink-0"
                onClick={(e) => {
                  e.stopPropagation();
                  dismissNotification(notification.uploadId);
                }}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
